import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  Switch,
  Slider,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAppDispatch } from '../../store';
import { updateAdaptiveFilters } from '../../store/slices/intelligentSearchSlice';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { AIExplanationDisplay } from './AIExplanationDisplay';
import type {
  DifficultyAdaptiveFilterProps,
  MasteryBasedFilter,
  MasteryFilterFeedback,
  MasteryState,
  AdaptiveFilter,
} from '@coffeeplexity/shared';

interface FilterControlProps {
  filter: MasteryBasedFilter;
  onUpdate: (filter: MasteryBasedFilter) => void;
  onFeedback: (feedback: MasteryFilterFeedback) => void;
  showExplanations: boolean;
}

const FilterControl: React.FC<FilterControlProps> = ({
  filter,
  onUpdate,
  onFeedback,
  showExplanations,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleDifficultyRangeChange = (values: number[]) => {
    const updatedFilter: MasteryBasedFilter = {
      ...filter,
      recommendedDifficultyRange: {
        ...filter.recommendedDifficultyRange,
        min: values[0],
        max: values[1],
      },
    };
    onUpdate(updatedFilter);
  };

  const handleChallengePreferenceChange = (
    preference: 'conservative' | 'moderate' | 'aggressive'
  ) => {
    const updatedFilter: MasteryBasedFilter = {
      ...filter,
      challengePreference: preference,
    };
    onUpdate(updatedFilter);
  };

  const handleContentVisibilityChange = (
    type: 'below' | 'at' | 'above',
    value: boolean
  ) => {
    const updatedFilter: MasteryBasedFilter = {
      ...filter,
      showBelowMastery: type === 'below' ? value : filter.showBelowMastery,
      showAtMastery: type === 'at' ? value : filter.showAtMastery,
      showAboveMastery: type === 'above' ? value : filter.showAboveMastery,
    };
    onUpdate(updatedFilter);
  };

  const handleFeedback = (
    tooEasy: boolean,
    tooHard: boolean,
    justRight: boolean,
    context?: string
  ) => {
    const feedback: MasteryFilterFeedback = {
      tooEasy,
      tooHard,
      justRight,
      timestamp: new Date().toISOString(),
      context,
    };
    onFeedback(feedback);
  };

  const getMasteryLevelColor = (mastery: number) => {
    if (mastery < 0.3) return '#F44336'; // Red for low mastery
    if (mastery < 0.7) return '#FF9800'; // Orange for medium mastery
    return '#4CAF50'; // Green for high mastery
  };

  const getChallengeColor = (preference: string) => {
    switch (preference) {
      case 'conservative':
        return '#4CAF50';
      case 'moderate':
        return '#FF9800';
      case 'aggressive':
        return '#F44336';
      default:
        return '#666';
    }
  };

  return (
    <>
      <View style={styles.filterControl}>
        <View style={styles.filterHeader}>
          <View style={styles.skillInfo}>
            <Text style={styles.skillName}>{filter.skillName}</Text>
            <View style={styles.masteryIndicator}>
              <View
                style={[
                  styles.masteryDot,
                  {
                    backgroundColor: getMasteryLevelColor(
                      filter.currentMastery
                    ),
                  },
                ]}
              />
              <Text style={styles.masteryText}>
                {Math.round(filter.currentMastery * 100)}% mastery
              </Text>
            </View>
          </View>

          <TouchableOpacity
            onPress={() => setShowDetails(true)}
            style={styles.detailsButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name='settings-outline' size={20} color='#666' />
          </TouchableOpacity>
        </View>

        <View style={styles.difficultyRange}>
          <Text style={styles.rangeLabel}>Recommended Difficulty Range</Text>
          <View style={styles.rangeDisplay}>
            <Text style={styles.rangeValue}>
              {filter.recommendedDifficultyRange.min.toFixed(1)} -{' '}
              {filter.recommendedDifficultyRange.max.toFixed(1)}
            </Text>
            <ConfidenceIndicator
              confidence={filter.recommendedDifficultyRange.confidence}
              size='small'
              showLabel={false}
            />
          </View>

          {showExplanations && (
            <Text style={styles.rangeExplanation} numberOfLines={2}>
              {filter.recommendedDifficultyRange.reasoning}
            </Text>
          )}
        </View>

        <View style={styles.challengePreference}>
          <Text style={styles.challengeLabel}>Challenge Level</Text>
          <View style={styles.challengeOptions}>
            {(['conservative', 'moderate', 'aggressive'] as const).map(
              option => (
                <TouchableOpacity
                  key={option}
                  style={[
                    styles.challengeOption,
                    filter.challengePreference === option &&
                      styles.challengeOptionActive,
                    { borderColor: getChallengeColor(option) },
                  ]}
                  onPress={() => handleChallengePreferenceChange(option)}
                >
                  <Text
                    style={[
                      styles.challengeOptionText,
                      filter.challengePreference === option &&
                        styles.challengeOptionTextActive,
                      { color: getChallengeColor(option) },
                    ]}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </Text>
                </TouchableOpacity>
              )
            )}
          </View>
        </View>

        <View style={styles.quickFeedback}>
          <Text style={styles.feedbackLabel}>
            How are the current recommendations?
          </Text>
          <View style={styles.feedbackButtons}>
            <TouchableOpacity
              style={[styles.feedbackButton, styles.feedbackButtonEasy]}
              onPress={() =>
                handleFeedback(true, false, false, 'Content too easy')
              }
            >
              <Ionicons name='arrow-down' size={14} color='#4CAF50' />
              <Text style={styles.feedbackButtonText}>Too Easy</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.feedbackButton, styles.feedbackButtonRight]}
              onPress={() =>
                handleFeedback(
                  false,
                  false,
                  true,
                  'Content difficulty just right'
                )
              }
            >
              <Ionicons name='checkmark' size={14} color='#2196F3' />
              <Text style={styles.feedbackButtonText}>Just Right</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.feedbackButton, styles.feedbackButtonHard]}
              onPress={() =>
                handleFeedback(false, true, false, 'Content too hard')
              }
            >
              <Ionicons name='arrow-up' size={14} color='#F44336' />
              <Text style={styles.feedbackButtonText}>Too Hard</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Filter Details Modal */}
      <Modal
        visible={showDetails}
        transparent
        animationType='slide'
        onRequestClose={() => setShowDetails(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  {filter.skillName} Filter Settings
                </Text>
                <TouchableOpacity
                  onPress={() => setShowDetails(false)}
                  style={styles.modalCloseButton}
                >
                  <Ionicons name='close' size={24} color='#666' />
                </TouchableOpacity>
              </View>

              <View style={styles.masteryOverview}>
                <Text style={styles.sectionTitle}>Current Mastery Level</Text>
                <View style={styles.masteryBar}>
                  <View
                    style={[
                      styles.masteryFill,
                      {
                        width: `${filter.currentMastery * 100}%`,
                        backgroundColor: getMasteryLevelColor(
                          filter.currentMastery
                        ),
                      },
                    ]}
                  />
                </View>
                <Text style={styles.masteryPercentage}>
                  {Math.round(filter.currentMastery * 100)}% mastery
                </Text>
              </View>

              <View style={styles.difficultySettings}>
                <Text style={styles.sectionTitle}>Difficulty Range</Text>
                <View style={styles.sliderContainer}>
                  <Text style={styles.sliderLabel}>
                    Range: {filter.recommendedDifficultyRange.min.toFixed(1)} -{' '}
                    {filter.recommendedDifficultyRange.max.toFixed(1)}
                  </Text>
                  {/* Note: React Native Slider doesn't support range by default */}
                  {/* This would need a custom range slider component */}
                  <View style={styles.sliderPlaceholder}>
                    <Text style={styles.sliderNote}>
                      Difficulty range is automatically adjusted based on your
                      mastery level
                    </Text>
                  </View>
                </View>

                <AIExplanationDisplay
                  explanation={{
                    title: 'Why this range was recommended',
                    description: filter.recommendedDifficultyRange.reasoning,
                    confidence: filter.recommendedDifficultyRange.confidence,
                    factors: [
                      `Current mastery: ${Math.round(filter.currentMastery * 100)}%`,
                      `Challenge preference: ${filter.challengePreference}`,
                      `Adaptation sensitivity: ${filter.adaptationSensitivity}`,
                    ],
                  }}
                  compact
                />
              </View>

              <View style={styles.contentVisibility}>
                <Text style={styles.sectionTitle}>Content Visibility</Text>
                <Text style={styles.sectionDescription}>
                  Choose what difficulty levels to show relative to your mastery
                </Text>

                <View style={styles.visibilityOption}>
                  <View style={styles.visibilityInfo}>
                    <Text style={styles.visibilityLabel}>
                      Below Mastery Level
                    </Text>
                    <Text style={styles.visibilityDescription}>
                      Content easier than your current skill level
                    </Text>
                  </View>
                  <Switch
                    value={filter.showBelowMastery}
                    onValueChange={value =>
                      handleContentVisibilityChange('below', value)
                    }
                    trackColor={{ false: '#ccc', true: '#4CAF50' }}
                    thumbColor='#fff'
                  />
                </View>

                <View style={styles.visibilityOption}>
                  <View style={styles.visibilityInfo}>
                    <Text style={styles.visibilityLabel}>At Mastery Level</Text>
                    <Text style={styles.visibilityDescription}>
                      Content matching your current skill level
                    </Text>
                  </View>
                  <Switch
                    value={filter.showAtMastery}
                    onValueChange={value =>
                      handleContentVisibilityChange('at', value)
                    }
                    trackColor={{ false: '#ccc', true: '#FF9800' }}
                    thumbColor='#fff'
                  />
                </View>

                <View style={styles.visibilityOption}>
                  <View style={styles.visibilityInfo}>
                    <Text style={styles.visibilityLabel}>
                      Above Mastery Level
                    </Text>
                    <Text style={styles.visibilityDescription}>
                      Challenging content to push your skills forward
                    </Text>
                  </View>
                  <Switch
                    value={filter.showAboveMastery}
                    onValueChange={value =>
                      handleContentVisibilityChange('above', value)
                    }
                    trackColor={{ false: '#ccc', true: '#F44336' }}
                    thumbColor='#fff'
                  />
                </View>
              </View>

              <View style={styles.adaptationSettings}>
                <Text style={styles.sectionTitle}>Adaptation Settings</Text>
                <View style={styles.adaptationInfo}>
                  <Text style={styles.adaptationLabel}>Sensitivity</Text>
                  <Text style={styles.adaptationValue}>
                    {Math.round(filter.adaptationSensitivity * 100)}%
                  </Text>
                </View>
                <Text style={styles.adaptationDescription}>
                  How quickly the filter adapts to your performance and feedback
                </Text>
              </View>

              {filter.metadata.effectivenessScore > 0 && (
                <View style={styles.effectivenessSection}>
                  <Text style={styles.sectionTitle}>Filter Effectiveness</Text>
                  <ConfidenceIndicator
                    confidence={filter.metadata.effectivenessScore}
                    size='large'
                    showLabel={true}
                    label='Effectiveness Score'
                  />
                  <Text style={styles.effectivenessDescription}>
                    Based on your engagement with filtered content
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
};

export const DifficultyAdaptiveFilter: React.FC<
  DifficultyAdaptiveFilterProps
> = ({
  masteryStates,
  currentFilters,
  onFilterChange,
  onFeedback,
  adaptationEnabled = true,
  showExplanations = true,
}) => {
  const dispatch = useAppDispatch();
  const [localFilters, setLocalFilters] =
    useState<MasteryBasedFilter[]>(currentFilters);

  useEffect(() => {
    setLocalFilters(currentFilters);
  }, [currentFilters]);

  const handleFilterUpdate = (updatedFilter: MasteryBasedFilter) => {
    const newFilters = localFilters.map(filter =>
      filter.skillId === updatedFilter.skillId ? updatedFilter : filter
    );
    setLocalFilters(newFilters);
    onFilterChange(newFilters);

    // Update adaptive filters in the store
    if (adaptationEnabled) {
      dispatch(
        updateAdaptiveFilters({
          userId: 'current-user', // TODO: Get from auth state
          masteryStates,
          userPreferences: {
            filters: newFilters,
          },
        })
      );
    }
  };

  const handleFilterFeedback = (
    filterId: string,
    feedback: MasteryFilterFeedback
  ) => {
    onFeedback(filterId, feedback);

    // Update filter metadata with feedback
    const updatedFilters = localFilters.map(filter => {
      if (filter.skillId === filterId) {
        return {
          ...filter,
          metadata: {
            ...filter.metadata,
            userFeedback: feedback,
            lastUpdated: new Date().toISOString(),
          },
        };
      }
      return filter;
    });

    setLocalFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  if (localFilters.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Ionicons name='options-outline' size={48} color='#ccc' />
        <Text style={styles.emptyStateText}>No adaptive filters available</Text>
        <Text style={styles.emptyStateSubtext}>
          Complete skill assessments to enable personalized filtering
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Adaptive Difficulty Filters</Text>
        <Text style={styles.subtitle}>
          Automatically adjusted based on your skill mastery levels
        </Text>

        {adaptationEnabled && (
          <View style={styles.adaptationStatus}>
            <Ionicons name='sync' size={16} color='#4CAF50' />
            <Text style={styles.adaptationStatusText}>
              Auto-adaptation enabled
            </Text>
          </View>
        )}
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.filtersList}
      >
        {localFilters.map(filter => (
          <FilterControl
            key={filter.skillId}
            filter={filter}
            onUpdate={handleFilterUpdate}
            onFeedback={feedback =>
              handleFilterFeedback(filter.skillId, feedback)
            }
            showExplanations={showExplanations}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  adaptationStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  adaptationStatusText: {
    fontSize: 12,
    color: '#4CAF50',
    marginLeft: 4,
    fontWeight: '500',
  },
  filtersList: {
    flex: 1,
  },
  filterControl: {
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  filterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  skillInfo: {
    flex: 1,
  },
  skillName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  masteryIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  masteryDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  masteryText: {
    fontSize: 12,
    color: '#666',
  },
  detailsButton: {
    padding: 4,
  },
  difficultyRange: {
    marginBottom: 16,
  },
  rangeLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  rangeDisplay: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  rangeValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2196F3',
  },
  rangeExplanation: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  challengePreference: {
    marginBottom: 16,
  },
  challengeLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  challengeOptions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  challengeOption: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    marginHorizontal: 4,
    alignItems: 'center',
  },
  challengeOptionActive: {
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
  },
  challengeOptionText: {
    fontSize: 12,
    fontWeight: '600',
  },
  challengeOptionTextActive: {
    color: '#2196F3',
  },
  quickFeedback: {
    marginTop: 8,
  },
  feedbackLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 8,
  },
  feedbackButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  feedbackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
    borderWidth: 1,
    flex: 1,
    marginHorizontal: 2,
    justifyContent: 'center',
  },
  feedbackButtonEasy: {
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
  feedbackButtonRight: {
    borderColor: '#2196F3',
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
  },
  feedbackButtonHard: {
    borderColor: '#F44336',
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
  },
  feedbackButtonText: {
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 2,
    color: '#333',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#666',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 40,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  modalCloseButton: {
    padding: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
    marginTop: 20,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  masteryOverview: {
    marginBottom: 20,
  },
  masteryBar: {
    height: 12,
    backgroundColor: '#e0e0e0',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 8,
  },
  masteryFill: {
    height: '100%',
    borderRadius: 6,
  },
  masteryPercentage: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  difficultySettings: {
    marginBottom: 20,
  },
  sliderContainer: {
    marginBottom: 16,
  },
  sliderLabel: {
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
  },
  sliderPlaceholder: {
    backgroundColor: '#f5f5f5',
    padding: 16,
    borderRadius: 8,
  },
  sliderNote: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  contentVisibility: {
    marginBottom: 20,
  },
  visibilityOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  visibilityInfo: {
    flex: 1,
    marginRight: 16,
  },
  visibilityLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  visibilityDescription: {
    fontSize: 12,
    color: '#666',
  },
  adaptationSettings: {
    marginBottom: 20,
  },
  adaptationInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  adaptationLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  adaptationValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2196F3',
  },
  adaptationDescription: {
    fontSize: 12,
    color: '#666',
  },
  effectivenessSection: {
    alignItems: 'center',
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  effectivenessDescription: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
  },
});

export default DifficultyAdaptiveFilter;
